/**
 * "In 2B, not in the books" — the bills waiting for a decision, with bulk
 * selection.
 * @file src/web/features/admin/components/NotInBooksList.tsx
 *
 * Nothing here matches or decides on its own. The person picks bills — one at
 * a time with the per-bill buttons, or many with the checkboxes — and chooses
 * Record, Hold back or Claim anyway. A bulk choice goes to the server in ONE
 * request (/api/gst/2b/decisions/bulk) and comes back as a count and a list of
 * the bills it could not apply to.
 *
 * Paged (50 a page) so a 2B with thousands of bills stays quick: only one page
 * of rows is ever in the DOM. "Select all" selects every bill in the current
 * list — all pages, after the search filter — not just the page in view.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Eye, Loader2, Search, Undo2, X } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../lib/admin-api';

export interface BillLine {
  section: string | null;
  supplierGstin: string | null; supplierName: string | null;
  invoiceNumber: string | null; invoiceDate: string | null;
  portal: { tax: number } | null;
  decision?: { action: 'hold' | 'claim'; reason: string | null; decidedBy: string | null; decidedAt: string } | null;
  needsAction?: boolean;
  doc?: { id: string } | null;
}

export const PAGE_SIZE = 50;
export const HOLD_REASONS = ['Goods or services not received', 'Disputed with the supplier', 'Invoice not in hand', 'Not our purchase'];

export const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');
export const keyOf = (l: BillLine) => `${String(l.section ?? 'b2b').toLowerCase()}|${String(l.supplierGstin ?? '').toUpperCase()}|${String(l.invoiceNumber ?? '').trim()}`;
const said = (e: unknown) => (e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Something went wrong');
const date = (d: string | null) => (!d ? '' : /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split('-').reverse().join('-') : d);

export type Bulk = 'record' | 'hold' | 'claim';
const LABEL: Record<Bulk, string> = { record: 'Record', hold: 'Hold back', claim: 'Claim anyway' };

export function NotInBooksList({ lines, period, entityId, onChanged, onRecord }: {
  lines: BillLine[]; period: string; entityId: string | null;
  /** After any decision: the page reloads the recon (and 3B). */
  onChanged: () => void;
  /** Open the single-bill record dialog. */
  onRecord: (line: BillLine) => void;
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The current list: everything, or what the search leaves.
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return lines;
    return lines.filter((l) => `${l.supplierName ?? ''} ${l.supplierGstin ?? ''} ${l.invoiceNumber ?? ''}`.toLowerCase().includes(t));
  }, [lines, q]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const at = Math.min(page, pages - 1);
  const shown = filtered.slice(at * PAGE_SIZE, at * PAGE_SIZE + PAGE_SIZE);

  const byKey = useMemo(() => new Map(lines.map((l) => [keyOf(l), l])), [lines]);
  const picked = [...selected].map((k) => byKey.get(k)).filter((l): l is BillLine => !!l);
  const pickedTax = picked.reduce((t, l) => t + Math.abs(l.portal?.tax ?? 0), 0);
  const allFilteredOn = filtered.length > 0 && filtered.every((l) => selected.has(keyOf(l)));

  const toggle = (l: BillLine) => setSelected((s) => { const n = new Set(s); const k = keyOf(l); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // Select all = every bill in the CURRENT list, across pages — after the search.
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allFilteredOn) for (const l of filtered) n.delete(keyOf(l));
    else for (const l of filtered) n.add(keyOf(l));
    return n;
  });
  const clear = () => setSelected(new Set());
  const done = (r: BulkResult | { error: string }) => {
    if ('error' in r) { setError(r.error); return; }
    setError(null); setResult(r);
    // Keep the ones that failed selected, so they can be dealt with next.
    setSelected(new Set(picked.filter((l) => r.failed.some((f) => sameBill(f, l))).map(keyOf)));
    onChanged();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search supplier, GSTIN or invoice no."
            aria-label="Search bills"
            className="w-full rounded-xl border border-ink-200 bg-paper py-2 pl-9 pr-3 text-sm focus:border-ink-400 focus:outline-none" />
        </div>
        <span className="text-xs text-ink-500">{filtered.length.toLocaleString('en-IN')} of {lines.length.toLocaleString('en-IN')} bill{lines.length === 1 ? '' : 's'}</span>
      </div>

      <BulkOutcome result={result} error={error} />

      <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
              <th className="w-10 px-4 py-2.5">
                <input type="checkbox" aria-label="Select all" checked={allFilteredOn} onChange={toggleAll} disabled={!filtered.length} />
              </th>
              <th className="px-3 py-2.5 font-medium">Supplier</th>
              <th className="px-3 py-2.5 font-medium">Invoice</th>
              <th className="px-3 py-2.5 text-right font-medium">Tax</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {shown.map((l) => {
              const k = keyOf(l);
              return (
                <tr key={k} className={`align-top ${selected.has(k) ? 'bg-highlighter-40/40' : 'hover:bg-ink-50/60'}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" aria-label={`Select ${l.invoiceNumber ?? ''}`} checked={selected.has(k)} onChange={() => toggle(l)} />
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-medium text-ink-900">{l.supplierName ?? '—'}</span>
                    {l.supplierGstin && <span className="block font-mono text-[11px] text-ink-400">{l.supplierGstin}</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-ink-800">{l.invoiceNumber ?? '—'}</span>
                    <span className="ml-2 text-[11px] text-ink-400">{date(l.invoiceDate)}</span>
                    <DecisionControls line={l} period={period} entityId={entityId} onChanged={onChanged} onRecord={() => onRecord(l)} />
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink-900">{inr(Math.abs(l.portal?.tax ?? 0))}</td>
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={4} className="px-5 py-6 text-center text-sm text-ink-500">No bills match the search.</td></tr>}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-ink-600">
          <button onClick={() => setPage(Math.max(0, at - 1))} disabled={at === 0} className="rounded-lg border border-ink-200 px-2.5 py-1 disabled:opacity-40">Previous</button>
          <span>Page {at + 1} of {pages}</span>
          <button onClick={() => setPage(Math.min(pages - 1, at + 1))} disabled={at >= pages - 1} className="rounded-lg border border-ink-200 px-2.5 py-1 disabled:opacity-40">Next</button>
        </div>
      )}

      <BulkDecisionBar picked={picked} pickedTax={pickedTax} period={period} entityId={entityId} onDone={done} onClear={clear} />
    </div>
  );
}

export interface BulkResult { action: Bulk; done: number; failed: { supplierGstin: string; invoiceNumber: string; error: string }[] }
export const sameBill = (f: { supplierGstin: string; invoiceNumber: string }, l: BillLine) =>
  String(f.supplierGstin).toUpperCase() === String(l.supplierGstin ?? '').toUpperCase() && String(f.invoiceNumber).trim() === String(l.invoiceNumber ?? '').trim();

/** The count and the failures of the last bulk choice, or why it was refused. */
export function BulkOutcome({ result, error }: { result: BulkResult | null; error: string | null }) {
  return (
    <>
      {result && (
        <div role="status" className={`rounded-xl border px-4 py-3 text-sm ${result.failed.length ? 'border-warn-300 bg-warn-50 text-warn-900' : 'border-ok-200 bg-ok-50 text-ok-800'}`}>
          <p className="font-semibold">{LABEL[result.action]}: done for {result.done.toLocaleString('en-IN')} bill{result.done === 1 ? '' : 's'}{result.failed.length ? `, ${result.failed.length} not` : ''}.</p>
          {!!result.failed.length && (
            <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto text-xs">
              {result.failed.map((f, i) => <li key={i}><span className="font-mono">{f.invoiceNumber}</span> · {f.supplierGstin} — {f.error}</li>)}
            </ul>
          )}
        </div>
      )}
      {error && <p role="alert" className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-2.5 text-sm text-danger-700">{error}</p>}
    </>
  );
}

/**
 * The sticky bar for a selection of 2B-only bills: Record, Hold back, Claim
 * anyway, Clear. One request to /api/gst/2b/decisions/bulk per choice; the
 * outcome goes back to the list through `onDone`.
 */
export function BulkDecisionBar({ picked, pickedTax, period, entityId, onDone, onClear }: {
  picked: BillLine[]; pickedTax: number; period: string; entityId: string | null;
  onDone: (r: BulkResult | { error: string }) => void; onClear: () => void;
}) {
  const [pending, setPending] = useState<Bulk | null>(null);   // the sub-form open in the bar
  const [reason, setReason] = useState('');
  const [received, setReceived] = useState(false);
  const [busy, setBusy] = useState<null | { action: Bulk; count: number }>(null);
  if (!picked.length) return null;
  const n = picked.length.toLocaleString('en-IN');

  const apply = async (action: Bulk) => {
    const bills = picked.map((l) => ({ section: l.section, supplierGstin: l.supplierGstin, invoiceNumber: l.invoiceNumber }));
    setBusy({ action, count: bills.length });
    try {
      const res = await adminFetch('/api/gst/2b/decisions/bulk', {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ period, action, bills, ...(action === 'hold' ? { reason } : {}), ...(action === 'record' ? { confirmed: received } : {}) }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || out.ok === false) throw new Error(out.error || out.message || `The server answered ${res.status}`);
      setPending(null); setReason(''); setReceived(false);
      onDone({ action, done: out.done ?? 0, failed: out.failed ?? [] });
    } catch (e) { onDone({ error: said(e) }); } finally { setBusy(null); }
  };

  return (
    <div role="region" aria-label="Selected bills" className="sticky bottom-4 z-20 rounded-2xl border border-ink-300 bg-paper px-4 py-3 shadow-lg">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink-900">{n} selected · ₹{inr(pickedTax)}</p>
        {busy ? (
          <p className="inline-flex items-center gap-2 text-sm text-ink-700">
            <Loader2 className="h-4 w-4 animate-spin" /> {LABEL[busy.action]} — {busy.count.toLocaleString('en-IN')} bill{busy.count === 1 ? '' : 's'}…
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(['record', 'hold', 'claim'] as Bulk[]).map((a) => (
              <button key={a} onClick={() => setPending(pending === a ? null : a)} aria-pressed={pending === a}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${a === 'record' ? 'bg-inkblack text-white' : 'border border-ink-300 bg-paper text-ink-700 hover:bg-ink-50'}`}>
                {LABEL[a]}
              </button>
            ))}
            <button onClick={() => { setPending(null); onClear(); }} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-ink-600 hover:text-ink-900"><X className="h-3.5 w-3.5" /> Clear selection</button>
          </div>
        )}
      </div>
      {!busy && pending === 'record' && (
        <div className="mt-2.5 space-y-2 border-t border-ink-100 pt-2.5 text-xs text-ink-700">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={received} onChange={(e) => setReceived(e.target.checked)} className="mt-0.5" />
            <span>The goods or services were received and the invoices are in hand for all {n} bills (Section 16(2)). Each is recorded to its supplier&rsquo;s usual purchase ledger.</span>
          </label>
          <button onClick={() => apply('record')} disabled={!received} className="rounded-full bg-inkblack px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Record {n}</button>
        </div>
      )}
      {!busy && pending === 'hold' && (
        <div className="mt-2.5 space-y-2 border-t border-ink-100 pt-2.5 text-xs text-ink-700">
          <p>The credit goes to 4B(2). Reason (optional), for all of them:</p>
          <div className="flex flex-wrap gap-1">
            {HOLD_REASONS.map((r) => (
              <button key={r} onClick={() => setReason(r)} className={`rounded-full border px-2 py-0.5 ${reason === r ? 'border-ink-900 font-semibold' : 'border-ink-200 text-ink-600'}`}>{r}</button>
            ))}
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Or write a reason" className="w-full max-w-md rounded-lg border border-ink-200 px-2 py-1 focus:outline-none" />
          <button onClick={() => apply('hold')} className="rounded-full bg-inkblack px-3 py-1.5 text-xs font-semibold text-white">Hold back {n}</button>
        </div>
      )}
      {!busy && pending === 'claim' && (
        <div className="mt-2.5 flex flex-wrap items-start gap-2 border-t border-ink-100 pt-2.5 text-xs text-warn-900">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">The credit is claimed although the books have no entry for these bills. If goods were not received, or a bill is not yours, it is reversible with interest.</span>
          <button onClick={() => apply('claim')} className="rounded-full border border-warn-500 bg-paper px-3 py-1.5 font-semibold">Claim anyway for {n}</button>
        </div>
      )}
    </div>
  );
}

/** Record / Hold back / Claim anyway, for one 2B bill the books do not have. */
export function DecisionControls({ line, period, entityId, onChanged, onRecord, compact = false }: {
  line: BillLine; period: string; entityId: string | null; onChanged: () => void; onRecord: () => void;
  /* Dense mode, for a row in the full 2B rather than a card in a list of
     bills to decide.
     
     There, "Needs action — not held back, decide before filing 3B" sits on
     every row of a column whose three buttons already say it, and "Hold back"
     and "Claim anyway" spend width on words the column heading supplies. A
     decision once MADE still shows either way: that is not derivable from the
     buttons. */
  compact?: boolean;
}) {
  const [mode, setMode] = useState<null | 'hold' | 'claim'>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async (action: 'hold' | 'claim' | 'undecided') => {
    setBusy(true); setErr(null);
    try {
      await adminFetchJson('/api/gst/2b/decision', {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ period, section: line.section, supplierGstin: line.supplierGstin, invoiceNumber: line.invoiceNumber, action, reason: action === 'hold' ? reason : null }),
      });
      setMode(null); setReason('');
      onChanged();
    } catch (e) { setErr(said(e)); } finally { setBusy(false); }
  };
  // whitespace-nowrap: "Claim anyway" was breaking across two lines inside
  // its own button before the row ever ran out of width.
  const btn = 'inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition disabled:opacity-50';
  const d = line.decision;
  const when = d ? new Date(d.decidedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return (
    <div className={`space-y-1.5 ${compact ? '' : 'mt-1.5'}`}>
      {d?.action === 'hold' && (
        <p className="text-[11px] text-ink-600"><span className="rounded bg-ink-100 px-1.5 py-0.5 font-semibold text-ink-700">Held back · 4B(2)</span> {d.reason ? `${d.reason} · ` : ''}decided {when}</p>
      )}
      {d?.action === 'claim' && (
        <p className="text-[11px] text-warn-800"><span className="rounded bg-warn-100 px-1.5 py-0.5 font-semibold">Claimed without a book entry</span> decided {when} — record the bill too, or the claim has nothing behind it.</p>
      )}
      {!d && !compact && <p className="text-[11px]"><span className="rounded bg-danger-50 px-1.5 py-0.5 font-semibold text-danger-700">Needs action</span> <span className="text-ink-500">not held back — decide before filing 3B</span></p>}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={onRecord} className={`${btn} border-ink-900 bg-inkblack text-white`}><Eye className="h-3 w-3" /> Record</button>
        {d?.action !== 'hold' && <button onClick={() => setMode(mode === 'hold' ? null : 'hold')} disabled={busy} className={`${btn} border-ink-300 bg-paper text-ink-700 hover:bg-ink-50`}>{compact ? 'Hold' : 'Hold back'}</button>}
        {d?.action !== 'claim' && <button onClick={() => setMode(mode === 'claim' ? null : 'claim')} disabled={busy} className={`${btn} border-ink-300 bg-paper text-ink-700 hover:bg-ink-50`}>{compact ? 'Claim' : 'Claim anyway'}</button>}
        {d && <button onClick={() => send('undecided')} disabled={busy} className={`${btn} border-ink-200 bg-paper text-ink-500 hover:bg-ink-50`}><Undo2 className="h-3 w-3" /> Undo</button>}
      </div>
      {mode === 'hold' && (
        <div className="space-y-1.5 rounded-lg border border-ink-200 bg-ink-50/60 p-2">
          <p className="text-[11px] text-ink-600">The credit goes to 4B(2) for this return. Reason (optional):</p>
          <div className="flex flex-wrap gap-1">
            {HOLD_REASONS.map((r) => (
              <button key={r} onClick={() => setReason(r)} className={`rounded-full border px-2 py-0.5 text-[11px] ${reason === r ? 'border-ink-900 bg-paper font-semibold' : 'border-ink-200 bg-paper text-ink-600'}`}>{r}</button>
            ))}
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Or write a reason" className="w-full rounded-lg border border-ink-200 bg-paper px-2 py-1 text-xs focus:outline-none" />
          <button onClick={() => send('hold')} disabled={busy} className={`${btn} border-ink-900 bg-inkblack text-white`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />} Hold back</button>
        </div>
      )}
      {mode === 'claim' && (
        <div className="space-y-1.5 rounded-lg border border-warn-300 bg-warn-50 p-2 text-[11px] text-warn-900">
          <p className="flex items-start gap-1"><AlertTriangle className="mt-px h-3 w-3 shrink-0" /> The credit is claimed although the books have no entry for this bill. If the goods were not received, or the bill is not yours, it is reversible with interest. Record it in the books when you can.</p>
          <button onClick={() => send('claim')} disabled={busy} className={`${btn} border-warn-500 bg-paper text-warn-900`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />} Claim anyway</button>
        </div>
      )}
      {err && <p className="text-[11px] text-danger-700">{err}</p>}
    </div>
  );
}
